import { useMemo, useState } from "react";
import { Check, ChevronsUpDown, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { TierBadge } from "@/components/shared/tier-badge";
import { cn } from "@/lib/utils";
import { formatApiError } from "@/lib/format-api-error";
import { useAssignmentsForProgram } from "@/features/admin/tiers/use-assignments-for-program";
import { useAssignPartnerTier } from "@/features/admin/tiers/use-assign-partner-tier";
import { useProgram } from "@/features/admin/tiers/use-program";
import { usePrograms } from "@/features/admin/tiers/use-programs";
import { useUnassignPartnerTier } from "@/features/admin/tiers/use-unassign-partner-tier";

// Phase 7 sub-bucket 13 carry-over fix from sub-bucket 10/12. Replaces the
// legacy `<TierBadge tier={partner.tier as never} />` chip in the partner
// detail header with an interactive popover that lets internal users:
//
//   - see the current default-program tier assignment (rendered with
//     badgePath + colorHex when set, falling back to the legacy enum chip)
//   - assign to a different tier in the default program
//   - remove the assignment entirely (Unassign)
//
// Sub-bucket 12 unblocked this by adding `useProgram` / `usePrograms` /
// `useAssignmentsForProgram` SPA hooks. The popover follows the
// `tier-assignments-tab.tsx` Popover + Command shape; we don't extract it
// into a shared component yet — two callers don't justify the abstraction.
//
// The legacy `partner.tier` enum stays a fallback so a partner with no
// default-program assignment still renders a meaningful chip (mirrors the
// 0038 sync trigger semantics).
export function PartnerTierAssignmentPopover({
  partnerId,
}: {
  partnerId: string;
}) {
  const programsQuery = usePrograms();
  const defaultProgram = useMemo(
    () => (programsQuery.data ?? []).find((p) => p.is_default) ?? null,
    [programsQuery.data],
  );
  const programId = defaultProgram?.id;
  const programDetail = useProgram(programId);
  const assignmentsQuery = useAssignmentsForProgram(programId);
  const assign = useAssignPartnerTier();
  const unassign = useUnassignPartnerTier();

  const [open, setOpen] = useState(false);

  const tiers = programDetail.data?.tiers ?? [];
  const assignment = useMemo(
    () =>
      (assignmentsQuery.data ?? []).find((a) => a.partner_id === partnerId) ??
      null,
    [assignmentsQuery.data, partnerId],
  );
  const assignedTier = useMemo(
    () => tiers.find((t) => t.id === assignment?.tier_id) ?? null,
    [tiers, assignment],
  );

  const busy = assign.isPending || unassign.isPending;

  function handleAssign(tierId: string) {
    if (!programId) return;
    const picked = tiers.find((t) => t.id === tierId);
    assign.mutate(
      {
        partner_id: partnerId,
        program_id: programId,
        tier_id: tierId,
        patchPartnerLegacyTier: picked?.legacy_tier ?? undefined,
      },
      {
        onSuccess: () => {
          toast.success(`Assigned to ${picked?.name ?? "tier"}`);
          setOpen(false);
        },
        onError: (err) => toast.error(formatApiError(err, "Failed to assign tier")),
      },
    );
  }

  function handleUnassign() {
    if (!programId) return;
    unassign.mutate(
      { programId, partnerId },
      {
        onSuccess: () => {
          toast.success("Assignment removed");
          setOpen(false);
        },
        onError: (err) =>
          toast.error(formatApiError(err, "Failed to remove assignment")),
      },
    );
  }

  // No default program → no assignment surface at all.
  if (programsQuery.isSuccess && !defaultProgram) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full border bg-muted/40 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
        No tier program
      </span>
    );
  }

  // Assignment-driven (0085): show the assigned tier, else "Unassigned". The
  // legacy partners.tier enum is no longer used as a display fallback.
  const triggerContent =
    assignedTier && assignment ? (
      <TierBadge
        assignment={{
          tierName: assignedTier.name,
          badgePath: assignedTier.badge_path,
          colorHex: assignedTier.color_hex,
          legacyTier: assignedTier.legacy_tier,
        }}
      />
    ) : (
      <span className="inline-flex items-center gap-1 rounded-full border bg-muted/40 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
        Unassigned
      </span>
    );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="Change tier assignment"
          className="inline-flex items-center gap-1 rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/60"
          disabled={programsQuery.isLoading}
        >
          {triggerContent}
          <ChevronsUpDown className="h-3 w-3 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-56 p-0">
        <Command>
          <CommandInput placeholder="Search tiers…" />
          <CommandList>
            <CommandEmpty>
              {tiers.length === 0
                ? "Default program has no tiers configured."
                : "No tiers match."}
            </CommandEmpty>
            {tiers.length > 0 && (
              <CommandGroup heading={defaultProgram?.name ?? "Default program"}>
                {tiers.map((t) => (
                  <CommandItem
                    key={t.id}
                    value={t.name}
                    onSelect={() => handleAssign(t.id)}
                    disabled={busy}
                  >
                    <span className="truncate">{t.name}</span>
                    {assignment?.tier_id === t.id && (
                      <Check className={cn("ml-auto h-3.5 w-3.5 opacity-100")} />
                    )}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            {assignment && (
              <>
                <CommandSeparator />
                <CommandGroup>
                  <CommandItem
                    value="__remove__"
                    onSelect={handleUnassign}
                    disabled={busy}
                    className="text-destructive data-[selected=true]:text-destructive"
                  >
                    {unassign.isPending ? (
                      <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <X className="mr-2 h-3.5 w-3.5" />
                    )}
                    Remove assignment
                  </CommandItem>
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
